import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  Settings,
  BarChart3,
  FileText,
  Calendar,
  LogOut,
  Home,
  Clock,
  User,
  CheckSquare
} from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import PunchButton from '../attendance/PunchButton';
import { AttendanceProvider } from '../../contexts/AttendanceContext';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import toast from 'react-hot-toast';

interface UserDashboardLayoutProps {
  children: React.ReactNode;
}

const NavigationItem: React.FC<{
  to: string;
  icon: React.ElementType;
  label: string;
  exact?: boolean;
}> = ({ to, icon: Icon, label, exact = false }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
        isActive
          ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`
    }
    end={exact}
  >
    <Icon className="w-5 h-5 mr-3" />
    {label}
  </NavLink>
);

export const UserDashboardLayout: React.FC<UserDashboardLayoutProps> = ({ children }) => {
  const { user, tenant, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Get user role for display
  const getUserRoleDisplay = () => {
    if (user?.roles?.includes('Manager')) return 'Manager';
    if (user?.roles?.includes('Staff')) return 'Staff';
    return 'User';
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      { to: '/user-dashboard', icon: Home, label: 'Dashboard', exact: true },
      { to: '/user-dashboard/tasks', icon: CheckSquare, label: 'My Tasks' },
      { to: '/user-dashboard/timesheet', icon: Clock, label: 'Timesheet' },
      { to: '/user-dashboard/attendance', icon: Calendar, label: 'Attendance' },
      { to: '/user-dashboard/settings', icon: Settings, label: 'Settings' },
    ];

    // Add manager-specific items
    if (user?.roles?.includes('Manager')) {
      baseItems.splice(3, 0,
        { to: '/user-dashboard/team', icon: Users, label: 'Team Management' },
        { to: '/user-dashboard/leave-approval', icon: FileText, label: 'Leave Approval' },
        { to: '/user-dashboard/reports', icon: BarChart3, label: 'Reports' }
      );
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  return (
    <AttendanceProvider>
      <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Building2 className="w-8 h-8 text-primary-500 mr-3" />
            <div>
              <h1 className="text-lg font-bold text-gray-900">ERPCA</h1>
              <p className="text-sm text-gray-600">{getUserRoleDisplay()}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigationItems.map((item) => (
            <NavigationItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              exact={item.exact}
            />
          ))}
        </nav>

        {/* Punch Button */}
        <div className="p-4">
          <PunchButton className="w-full justify-center" />
        </div>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-primary-600 font-medium">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">{tenant?.name}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <button
              onClick={() => navigate('/user-dashboard/profile')}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User className="w-4 h-4 mr-2" />
              My Profile
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex justify-end items-center p-4 border-b border-gray-100 bg-white">
          <NotificationBell />
        </div>
        {children}
      </div>
    </div>
    </AttendanceProvider>
  );
};
